import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { lockBodyScroll } from '../../utils/bodyScrollLock';
import { playSound } from '../../utils/utils';
import {
  CORE_PORTAL_ENTRY_SOUND,
  WARDEN_PORTAL_ENTRY_SOUND,
} from '../../utils/soundEffects';
import PortalScene from './PortalScene';
import { PORTAL_ENTRY_MS, getPortalTransitDuration } from './portalTravel';
import { getPortalWarpProfile } from './portalWarpProfiles';
import './PortalWarp.css';

const PortalTransit = ({
  route,
  dieId,
  floor,
  onComplete,
  skipLabel = 'Skip to board',
}) => {
  const dialogRef = useRef(null);
  const skipRef = useRef(null);
  const completeRef = useRef(onComplete);
  const completedRef = useRef(false);
  const stopEntrySoundRef = useRef(null);
  completeRef.current = onComplete;
  // The unlock celebration can refresh route metadata during the flight.
  // Keep this journey's scene stable so that update cannot restart its clock.
  const routes = useRef([route]).current;
  const profile = getPortalWarpProfile(routes[0]);
  const entrySound =
    routes[0].routeClass === 'core' || routes[0].kind === 'Core'
      ? CORE_PORTAL_ENTRY_SOUND
      : profile.id === 'warden'
      ? WARDEN_PORTAL_ENTRY_SOUND
      : '/sounds/enter-portal-6.mp3';
  const duration = getPortalTransitDuration(routes[0]);
  const [warpStage, setWarpStage] = useState(0);
  const reduceMotion = !!window.matchMedia?.('(prefers-reduced-motion: reduce)')
    .matches;
  const finish = () => {
    if (completedRef.current) return;
    completedRef.current = true;
    if (typeof stopEntrySoundRef.current === 'function')
      stopEntrySoundRef.current();
    stopEntrySoundRef.current = null;
    completeRef.current();
  };
  const finishRef = useRef(finish);
  finishRef.current = finish;

  useEffect(() => {
    const releaseScroll = lockBodyScroll();
    const previousFocus = document.activeElement;
    const root = document.getElementById('root');
    const previousInert = root?.inert;
    const previousHidden = root?.getAttribute('aria-hidden');
    dialogRef.current?.focus({ preventScroll: true });
    if (root) {
      root.inert = true;
      root.setAttribute('aria-hidden', 'true');
    }
    const timer = setTimeout(() => finishRef.current(), duration);
    const stageTimers = [
      setTimeout(() => {
        if (!completedRef.current) setWarpStage(1);
      }, PORTAL_ENTRY_MS),
      setTimeout(() => {
        if (!completedRef.current) setWarpStage(2);
      }, duration * 0.72),
    ];
    const entryTimer = setTimeout(() => {
      if (!completedRef.current)
        stopEntrySoundRef.current = playSound(entrySound, {
          preventOverlap: true,
        });
    }, PORTAL_ENTRY_MS);
    const keyDown = (event) => {
      if (event.key === 'Escape') finishRef.current();
      if (event.key === 'Tab') {
        event.preventDefault();
        skipRef.current?.focus({ preventScroll: true });
      }
    };
    document.addEventListener('keydown', keyDown);
    return () => {
      clearTimeout(timer);
      clearTimeout(entryTimer);
      stageTimers.forEach(clearTimeout);
      if (typeof stopEntrySoundRef.current === 'function')
        stopEntrySoundRef.current();
      stopEntrySoundRef.current = null;
      document.removeEventListener('keydown', keyDown);
      releaseScroll();
      if (root) {
        root.inert = previousInert;
        if (previousHidden === null) root.removeAttribute('aria-hidden');
        else root.setAttribute('aria-hidden', previousHidden);
      }
      if (previousFocus?.isConnected)
        previousFocus.focus?.({ preventScroll: true });
    };
  }, [duration, entrySound]);

  return createPortal(
    <section
      ref={dialogRef}
      tabIndex={-1}
      className={`coreboundPortalTransit warp-${profile.id} at-warp-${warpStage}`}
      data-warp-profile={profile.id}
      style={{
        '--transit-duration': `${duration}ms`,
        '--warp-primary': profile.primary,
        '--warp-secondary': profile.secondary,
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="portal-transit-title"
      aria-describedby="portal-transit-description"
    >
      <PortalScene
        routes={routes}
        transit
        dieId={dieId}
        reduceMotion={reduceMotion}
      />
      <div className="portalTransitVignette" aria-hidden="true" />
      <div className="portalTransitArrival" aria-hidden="true" />
      <header className="portalTransitHeader">
        <span>COREBOUND / SECTOR {String(floor).padStart(2, '0')}</span>
        <span className="portalTransitSignal">
          {profile.id === 'core'
            ? ['FINAL SIGNAL LOCKED', 'CONTAINMENT BREACHED', 'ORIGIN AHEAD'][
                warpStage
              ]
            : profile.id === 'warden'
            ? ['WARDEN GATE LOCKED', 'SIEGE SEALS BREAKING', 'CITADEL AHEAD'][
                warpStage
              ]
            : ['SIGNAL LOCKED', 'CROSSING THE VEIL', 'DESTINATION ACQUIRED'][
                warpStage
              ]}
        </span>
      </header>
      <div className="portalTransitDestination">
        <span>{route.kind} portal</span>
        <h2 id="portal-transit-title">{route.name}</h2>
        <p id="portal-transit-description">{profile.description}</p>
      </div>
      <div className="portalWarpDesignation" aria-hidden="true">
        <span>{profile.label}</span>
        <i />
        <small>
          {profile.id === 'core'
            ? 'THE NETWORK ENDS HERE'
            : 'FOLLOW YOUR SIGNAL'}
        </small>
      </div>
      <button ref={skipRef} className="portalTransitSkip" onClick={finish}>
        {skipLabel} <span aria-hidden="true">→</span>
      </button>
      <div className="portalTransitProgress" aria-hidden="true">
        <i />
      </div>
    </section>,
    document.body
  );
};

export default PortalTransit;
